import { useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { OWNER_TIMEZONE, clientName } from '@diana/shared'
import { api, ApiError, type CourseOutline, type PlanClassItem, type StudioPlan, type StudioPlanItem } from '../../lib/api.js'
import { Button, ErrorLine, FieldLabel, More, Status } from '../../classroom/ui.js'

/**
 * „Plan nauki” on the client card: the lessons Diana uses in her own classes
 * with this student (alone, in a pair or a group). She builds the list,
 * releases items one by one as they get there, and opens each lesson in the
 * teacher view. The students see the goal, the notes and the released items.
 */

const plural = (n: number, one: string, few: string, many: string) =>
  n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14) ? few : many

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name

const local = (iso: string) => DateTime.fromISO(iso).setZone(OWNER_TIMEZONE).setLocale('pl')

/** „czw 23.10, 18:00” */
const classLabel = (iso: string) => `${local(iso).toFormat('ccc').replace(/\.$/, '')} ${local(iso).toFormat('dd.LL, HH:mm')}`

/** The teacher view of one lesson, for this student and this plan. */
export function teacherLink(courseSlug: string, lessonSlug: string, clientId: string | null, planId: string): string {
  const q = new URLSearchParams()
  if (clientId) q.set('uczen', clientId)
  q.set('plan', planId)
  return `/studio/lekcja/${courseSlug}/${lessonSlug}?${q.toString()}`
}

/** „Plan: Lekcja 3 · …” under a class on Start and in the calendar. */
export function PlanClassLine({ item, clientId }: { item: PlanClassItem; clientId: string | null }) {
  const lesson = item.lessonSlug !== null
  const text = lesson ? `Lekcja ${item.lessonNumber} · ${item.lessonTitle}` : item.courseTitle
  const to = lesson ? teacherLink(item.courseSlug, item.lessonSlug!, clientId, item.planId) : clientId ? `/studio/klienci/${clientId}` : null
  return (
    <span className="flex min-w-0 items-baseline gap-1 text-class-gloss">
      <span className="shrink-0 text-class-ink-soft">Plan:</span>
      {to ? (
        <Link to={to} className="flex min-w-0 font-semibold">
          <span className="truncate">{text}</span>
          <span className="shrink-0">&nbsp;›</span>
        </Link>
      ) : (
        <span className="truncate">{text}</span>
      )}
      {!item.released && <span className="shrink-0 text-class-ink-faint">· jeszcze {lesson ? 'nieudostępniona' : 'nieudostępniony'}</span>}
    </span>
  )
}

function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.message : ''
  if (code === 'duplicate') return 'Ta lekcja już jest w planie.'
  if (code === 'items_changed') return 'Plan zmienił się w międzyczasie. Spróbuj jeszcze raz.'
  if (code === 'booking_not_found') return 'Tych zajęć już nie ma.'
  return 'Nie udało się zapisać.'
}

/** Keeps every card that shows a plan, Start and the calendar in step after a change. */
function usePlanCache(clientId: string) {
  const queryClient = useQueryClient()
  const key = ['studio', 'client', clientId, 'plans']
  const invalidate = () => {
    // A shared plan sits on every member's card.
    queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'studio' && q.queryKey[1] === 'client' && q.queryKey[3] === 'plans' })
    queryClient.invalidateQueries({ queryKey: ['studio', 'dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'calendar'] })
  }
  return {
    key,
    apply: (plan: StudioPlan) => {
      // Taking this student out of a plan takes the plan off this card.
      const mine = plan.members.some((m) => m.clientId === clientId)
      queryClient.setQueryData<StudioPlan[]>(key, (old = []) =>
        !mine
          ? old.filter((p) => p.id !== plan.id)
          : old.some((p) => p.id === plan.id)
            ? old.map((p) => (p.id === plan.id ? plan : p))
            : [plan, ...old],
      )
      invalidate()
    },
    drop: (planId: string) => {
      queryClient.setQueryData<StudioPlan[]>(key, (old = []) => old.filter((p) => p.id !== planId))
      invalidate()
    },
  }
}

type Cache = ReturnType<typeof usePlanCache>

export function PlanSection({ clientId }: { clientId: string }) {
  const cache = usePlanCache(clientId)
  const plans = useQuery({ queryKey: cache.key, queryFn: () => api.studio.clientPlans(clientId) })
  const outline = useQuery({ queryKey: ['studio', 'course-outline'], queryFn: api.studio.courseOutline, enabled: (plans.data?.length ?? 0) > 0 })
  const [fresh, setFresh] = useState<string | null>(null)
  const [showEnded, setShowEnded] = useState(false)

  const create = useMutation({
    mutationFn: () => api.studio.createPlan([clientId]),
    onSuccess: (plan) => {
      setFresh(plan.id)
      cache.apply(plan)
    },
  })
  const restore = useMutation({
    mutationFn: (planId: string) => api.studio.updatePlan(planId, { archived: false }),
    onSuccess: cache.apply,
  })

  const all = plans.data ?? []
  const live = all.filter((p) => !p.archivedAt).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const ended = all.filter((p) => p.archivedAt).sort((a, b) => (b.archivedAt ?? '').localeCompare(a.archivedAt ?? ''))

  return (
    <section className="flex flex-col">
      <div className="flex items-baseline border-b border-class-line pb-3">
        <h2 className="cl-subheading m-0">Plan nauki</h2>
        {live.length > 0 && (
          <button type="button" className="cl-link-quiet ml-auto text-class-gloss" disabled={create.isPending} onClick={() => create.mutate()}>
            + Nowy plan
          </button>
        )}
      </div>

      {plans.isLoading ? (
        <p className="m-0 py-4 text-class-gloss text-class-ink-faint">Wczytuję…</p>
      ) : plans.isError ? (
        <div className="py-4">
          <ErrorLine>Nie udało się wczytać planu.</ErrorLine>
        </div>
      ) : live.length === 0 ? (
        <div className="flex flex-col gap-4 py-4">
          <p className="m-0 text-class-gloss text-class-ink-soft">
            Lekcje z kursów, które przerabiacie na zajęciach. Uczeń widzi tylko te, które udostępnisz.
          </p>
          <Button variant="secondary" className="self-start" disabled={create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Tworzę…' : 'Utwórz plan'}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-8 [&>*+*]:border-t [&>*+*]:border-class-line [&>*+*]:pt-2">
          {live.map((plan) => (
            <PlanCard key={plan.id} plan={plan} clientId={clientId} outline={outline.data} cache={cache} startWithGoal={fresh === plan.id} />
          ))}
        </div>
      )}
      {create.isError && <ErrorLine>Nie udało się utworzyć planu.</ErrorLine>}

      {ended.length > 0 && (
        <div className="mt-6 flex flex-col">
          <button type="button" className="cl-link-quiet self-start text-class-gloss" aria-expanded={showEnded} onClick={() => setShowEnded((s) => !s)}>
            Zakończone plany ({ended.length}) {showEnded ? '▴' : '▾'}
          </button>
          {showEnded && (
            <div className="mt-2 flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
              {ended.map((p) => (
                <div key={p.id} className="flex min-h-14 items-center gap-4 py-2">
                  <span className="min-w-0 flex-1 leading-snug">
                    <span className="block font-semibold">{p.goal ? `Cel: ${p.goal}` : 'Plan bez celu'}</span>
                    <span className="block text-class-gloss text-class-ink-soft">
                      {p.items.length} {plural(p.items.length, 'pozycja', 'pozycje', 'pozycji')}
                      {p.members.length > 1 && ` · ${p.members.map((m) => firstName(m.name)).join(', ')}`}
                      {p.archivedAt && ` · zakończony ${local(p.archivedAt).toFormat('dd.LL.yyyy')}`}
                    </span>
                  </span>
                  <button type="button" className="cl-link text-class-gloss" disabled={restore.isPending} onClick={() => restore.mutate(p.id)}>
                    Przywróć
                  </button>
                </div>
              ))}
            </div>
          )}
          {restore.isError && <ErrorLine>Nie udało się przywrócić planu.</ErrorLine>}
        </div>
      )}
    </section>
  )
}

/* ── one live plan ─────────────────────────────────────────────────────── */

function PlanCard({
  plan,
  clientId,
  outline,
  cache,
  startWithGoal,
}: {
  plan: StudioPlan
  clientId: string
  outline: CourseOutline | undefined
  cache: Cache
  startWithGoal: boolean
}) {
  const [adding, setAdding] = useState(false)
  const act = useMutation({ mutationFn: (call: () => Promise<StudioPlan>) => call(), onSuccess: cache.apply })
  const end = useMutation({ mutationFn: () => api.studio.updatePlan(plan.id, { archived: true }), onSuccess: cache.apply })
  const remove = useMutation({ mutationFn: () => api.studio.deletePlan(plan.id), onSuccess: () => cache.drop(plan.id) })
  const busy = act.isPending || end.isPending || remove.isPending

  const move = (index: number, delta: number) => {
    const ids = plan.items.map((i) => i.id)
    const [id] = ids.splice(index, 1)
    ids.splice(index + delta, 0, id!)
    act.mutate(() => api.studio.orderPlan(plan.id, ids))
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2 py-4">
        <Goal plan={plan} cache={cache} startOpen={startWithGoal} />
        <Members plan={plan} clientId={clientId} cache={cache} />
      </div>

      {plan.items.length === 0 ? (
        <p className="m-0 border-y border-class-line py-4 text-class-gloss text-class-ink-soft">
          Plan jest pusty. Dodaj lekcje albo cały kurs — uczeń zobaczy je dopiero, gdy je udostępnisz.
        </p>
      ) : (
        <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
          {plan.items.map((item, i) => (
            <ItemRow
              key={item.id}
              item={item}
              plan={plan}
              clientId={clientId}
              outline={outline}
              busy={busy}
              onMove={(delta) => move(i, delta)}
              canUp={i > 0}
              canDown={i < plan.items.length - 1}
              run={(call) => act.mutate(call)}
            />
          ))}
        </div>
      )}
      {act.isError && (
        <div className="pt-3">
          <ErrorLine>{errorText(act.error)}</ErrorLine>
        </div>
      )}

      {adding ? (
        <AddItem plan={plan} outline={outline} cache={cache} onClose={() => setAdding(false)} />
      ) : (
        <button type="button" className="cl-link self-start py-3 text-class-gloss" onClick={() => setAdding(true)}>
          + Dodaj lekcję albo kurs
        </button>
      )}

      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-class-label">
        <button
          type="button"
          className="cl-link-quiet"
          disabled={busy}
          onClick={() =>
            window.confirm(
              'Zakończyć ten plan? Uczniowie stracą dostęp do jego lekcji (kursy, które mają aktywne, zostają). Plan zostanie w historii — możesz go przywrócić.',
            ) && end.mutate()
          }
        >
          Zakończ plan
        </button>
        <button
          type="button"
          className="cl-link-quiet"
          disabled={busy}
          onClick={() =>
            window.confirm(
              'Usunąć ten plan na zawsze? Zniknie cała lista z notatkami i nie da się go przywrócić, a uczniowie stracą dostęp do jego lekcji. Postęp w lekcjach zostaje. Jeśli chcesz go tylko zamknąć, wybierz „Zakończ plan”.',
            ) && remove.mutate()
          }
        >
          Usuń plan
        </button>
      </div>
      {(end.isError || remove.isError) && <ErrorLine>Nie udało się zapisać.</ErrorLine>}
    </div>
  )
}

/* ── the goal: „Cel: B1 w czerwcu” ─────────────────────────────────────── */

function Goal({ plan, cache, startOpen }: { plan: StudioPlan; cache: Cache; startOpen: boolean }) {
  const [editing, setEditing] = useState(startOpen)
  const [value, setValue] = useState(plan.goal)
  const save = useMutation({
    mutationFn: (goal: string) => api.studio.updatePlan(plan.id, { goal }),
    onSuccess: (p) => {
      cache.apply(p)
      setEditing(false)
    },
  })
  const id = `goal-${plan.id}`

  if (editing) {
    return (
      <form
        className="flex flex-col gap-1.5"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          save.mutate(value.trim())
        }}
      >
        <FieldLabel htmlFor={id}>Cel planu</FieldLabel>
        <div className="flex flex-wrap items-center gap-3">
          <input id={id} className="cl-input min-w-0 flex-1" maxLength={500} autoFocus placeholder="np. B1 w czerwcu" value={value} onChange={(e) => setValue(e.target.value)} />
          <Button type="submit" variant="secondary" disabled={save.isPending}>
            Zapisz
          </Button>
          <button
            type="button"
            className="cl-link-quiet text-class-gloss"
            onClick={() => {
              setValue(plan.goal)
              setEditing(false)
            }}
          >
            Anuluj
          </button>
        </div>
        <p className="m-0 text-class-label text-class-ink-faint">Widzi to uczeń.</p>
        {save.isError && <ErrorLine>Nie udało się zapisać celu.</ErrorLine>}
      </form>
    )
  }
  if (!plan.goal) {
    return (
      <button type="button" className="cl-link self-start text-class-gloss" onClick={() => setEditing(true)}>
        + Dodaj cel
      </button>
    )
  }
  return (
    <p className="m-0 leading-snug">
      <span className="text-class-ink-soft">Cel:</span> <span className="font-semibold">{plan.goal}</span>{' '}
      <button
        type="button"
        className="cl-link-quiet ml-1 text-class-label"
        onClick={() => {
          setValue(plan.goal)
          setEditing(true)
        }}
      >
        zmień
      </button>
    </p>
  )
}

/* ── who shares the plan ───────────────────────────────────────────────── */

function Members({ plan, clientId, cache }: { plan: StudioPlan; clientId: string; cache: Cache }) {
  const [editing, setEditing] = useState(false)
  const [pick, setPick] = useState('')
  const clients = useQuery({ queryKey: ['studio', 'clients'], queryFn: api.studio.clients, enabled: editing })
  const save = useMutation({
    mutationFn: (ids: string[]) => api.studio.setPlanMembers(plan.id, ids),
    onSuccess: (p) => {
      setPick('')
      cache.apply(p)
    },
  })
  const ids = plan.members.map((m) => m.clientId)
  const others = (clients.data ?? [])
    .filter((c) => !ids.includes(c.id))
    .map((c) => ({ id: c.id, name: clientName(c), active: c.active }))
    .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name, 'pl'))
  const noLogin = plan.members.filter((m) => !m.hasLogin)

  return (
    <>
      {editing ? (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-class-label font-semibold text-class-ink-soft">Kto korzysta z planu</p>
          <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
            {plan.members.map((m) => (
              <div key={m.clientId} className="flex min-h-11 items-center gap-4 py-1.5 text-class-gloss">
                <span className="min-w-0 flex-1 truncate font-semibold">{m.name}</span>
                {!m.hasLogin && <span className="text-class-label text-class-ink-faint">bez konta</span>}
                <button
                  type="button"
                  className="cl-link-quiet text-class-label"
                  disabled={plan.members.length < 2 || save.isPending}
                  title={plan.members.length < 2 ? 'W planie musi zostać co najmniej jedna osoba.' : undefined}
                  onClick={() =>
                    window.confirm(`Usunąć ${m.name} z planu? ${m.clientId === clientId ? 'Plan zniknie z tej karty i ' : ''}${firstName(m.name)} przestanie go widzieć.`) &&
                    save.mutate(ids.filter((x) => x !== m.clientId))
                  }
                >
                  usuń
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor={`add-member-${plan.id}`} className="sr-only">
              Dodaj osobę
            </label>
            <select id={`add-member-${plan.id}`} className="cl-input min-w-0 flex-1" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">{clients.isLoading ? 'Wczytuję uczniów…' : 'Dodaj osobę…'}</option>
              {others.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.active ? '' : ' (nieaktywny)'}
                </option>
              ))}
            </select>
            <Button variant="secondary" disabled={!pick || save.isPending} onClick={() => save.mutate([...ids, pick])}>
              Dodaj
            </Button>
            <button type="button" className="cl-link-quiet text-class-gloss" onClick={() => setEditing(false)}>
              Gotowe
            </button>
          </div>
          {save.isError && <ErrorLine>Nie udało się zapisać.</ErrorLine>}
        </div>
      ) : plan.members.length > 1 ? (
        <p className="m-0 text-class-gloss text-class-ink-soft">
          Wspólny plan: {plan.members.map((m) => m.name).join(', ')}{' '}
          <button type="button" className="cl-link-quiet ml-1 text-class-label" onClick={() => setEditing(true)}>
            zmień
          </button>
        </p>
      ) : (
        <button type="button" className="cl-link-quiet self-start text-class-gloss" onClick={() => setEditing(true)}>
          + Dodaj osobę (para, grupa)
        </button>
      )}
      {noLogin.map((m) => (
        <p key={m.clientId} className="m-0 text-class-label text-class-ink-faint">
          {m.clientId === clientId ? (
            <>{m.name} nie zobaczy planu, dopóki nie ma konta — załóż je w bloku „Konto”.</>
          ) : (
            <>
              <Link to={`/studio/klienci/${m.clientId}`} className="font-semibold">
                {m.name}
              </Link>{' '}
              nie zobaczy planu, dopóki nie ma konta.
            </>
          )}
        </p>
      ))}
    </>
  )
}

/* ── one item of the plan ──────────────────────────────────────────────── */

function progressText(item: StudioPlanItem, p: StudioPlanItem['progress'][number]): string {
  if (item.lesson) {
    const state = p.completed > 0 ? '✓ ukończona' : p.started ? 'otwarta' : 'jeszcze nie otwarta'
    const hw = p.homework && p.homework.parts > 0 && (p.started || p.homework.done > 0) ? ` · praca domowa ${p.homework.done}/${p.homework.parts}` : ''
    return state + hw
  }
  if (p.completed === 0 && !p.started) return 'jeszcze nie zaczęty'
  return p.completed >= p.lessons && p.lessons > 0 ? `✓ ukończony, ${p.lessons} z ${p.lessons} lekcji` : `${p.completed} z ${p.lessons} lekcji`
}

function ItemRow({
  item,
  plan,
  clientId,
  outline,
  busy,
  canUp,
  canDown,
  onMove,
  run,
}: {
  item: StudioPlanItem
  plan: StudioPlan
  clientId: string
  outline: CourseOutline | undefined
  busy: boolean
  canUp: boolean
  canDown: boolean
  onMove: (delta: number) => void
  run: (call: () => Promise<StudioPlan>) => void
}) {
  const [editing, setEditing] = useState(false)
  const title = item.lesson ? `Lekcja ${item.lesson.number} · ${item.lesson.title}` : 'Cały kurs'
  // Polish needs the case and the gender: „Ukryć lekcję 3? … przestaną ją widzieć”.
  const name = item.lesson ? `lekcję ${item.lesson.number}` : `cały kurs „${item.course.title}”`
  const it = item.lesson ? { acc: 'ją', gen: 'niej', shared: 'udostępniona' } : { acc: 'go', gen: 'niego', shared: 'udostępniony' }
  const group = plan.members.length > 1
  const memberName = (id: string | null | undefined) => firstName(plan.members.find((m) => m.clientId === id)?.name ?? '')
  const classOwner = item.booking ? plan.classes.find((c) => c.id === item.booking!.id)?.clientId : null

  // A whole course: she picks which lesson to teach, by default the one after the first student's last.
  const lessons = outline?.find((c) => c.id === item.course.id)?.lessons ?? []
  const done = item.progress[0]?.completed ?? 0
  const [chosen, setChosen] = useState<string | null>(null)
  const lessonSlug = item.lesson ? item.lesson.slug : (chosen ?? lessons[Math.min(done, lessons.length - 1)]?.slug ?? null)

  const meta = [
    `${item.course.title}${item.course.published ? '' : ' (szkic)'}`,
    item.booking &&
      `zajęcia ${local(item.booking.startsAt).toFormat('dd.LL, HH:mm')}${group && classOwner ? ` (${memberName(classOwner)})` : ''}${item.booking.status === 'cancelled' ? ' — odwołane' : ''}`,
    item.lesson?.retired && 'lekcja wycofana z kursu',
  ].filter(Boolean)

  return (
    <div className="flex flex-col gap-2 py-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <span className="min-w-0 flex-1 basis-60 leading-snug">
          <span className="block font-semibold">{title}</span>
          <span className="block text-class-gloss text-class-ink-soft">{meta.join(' · ')}</span>
          {item.note && <span className="block text-class-gloss text-class-ink-soft">„{item.note}”</span>}
        </span>
        {item.releasedAt ? (
          <span className="flex min-h-10 items-center gap-3">
            <Status tone="ok">
              {it.shared} {local(item.releasedAt).toFormat('dd.LL')}
            </Status>
            <button
              type="button"
              className="cl-link-quiet text-class-label"
              disabled={busy}
              onClick={() =>
                window.confirm(`Ukryć ${name}? Uczniowie przestaną ${it.acc} widzieć i nie otworzą ${item.lesson ? 'jej' : 'go'}. Postęp zostaje.`) &&
                run(() => api.studio.updatePlanItem(item.id, { released: false }))
              }
            >
              ukryj
            </button>
          </span>
        ) : (
          <Button
            variant="secondary"
            className="min-h-10 px-[18px] text-class-gloss"
            disabled={busy}
            onClick={() => run(() => api.studio.updatePlanItem(item.id, { released: true }))}
          >
            Udostępnij
          </Button>
        )}
      </div>

      {item.progress.length > 0 && (
        <div className="flex flex-col">
          {item.progress.map((p) => (
            <p key={p.clientId} className="m-0 text-class-label text-class-ink-soft">
              <span className="font-semibold">{memberName(p.clientId)}:</span> {progressText(item, p)}
            </p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {!item.lesson && lessons.length > 0 && (
          <>
            <label htmlFor={`teach-${item.id}`} className="sr-only">
              Którą lekcję prowadzisz
            </label>
            <select
              id={`teach-${item.id}`}
              className="cl-input !min-h-9 w-auto min-w-0 max-w-[240px] text-class-gloss"
              value={lessonSlug ?? ''}
              onChange={(e) => setChosen(e.target.value)}
            >
              {lessons.map((l) => (
                <option key={l.id} value={l.slug}>
                  Lekcja {l.number} · {l.title}
                </option>
              ))}
            </select>
          </>
        )}
        {lessonSlug && (
          <More to={teacherLink(item.course.slug, lessonSlug, clientId, plan.id)} className="text-class-gloss">
            Prowadź lekcję
          </More>
        )}
        <span className="ml-auto flex items-center gap-4 text-class-label">
          <button type="button" className="cl-link-quiet" aria-label="Przesuń wyżej" title="Przesuń wyżej" disabled={busy || !canUp} onClick={() => onMove(-1)}>
            ↑
          </button>
          <button type="button" className="cl-link-quiet" aria-label="Przesuń niżej" title="Przesuń niżej" disabled={busy || !canDown} onClick={() => onMove(1)}>
            ↓
          </button>
          <button type="button" className="cl-link-quiet" aria-expanded={editing} onClick={() => setEditing((e) => !e)}>
            edytuj
          </button>
          <button
            type="button"
            className="cl-link-quiet"
            disabled={busy}
            onClick={() =>
              window.confirm(`Usunąć ${name} z planu?${item.releasedAt ? ` Uczniowie stracą do ${it.gen} dostęp.` : ''}`) &&
              run(() => api.studio.deletePlanItem(item.id))
            }
          >
            usuń
          </button>
        </span>
      </div>

      {editing && (
        <ItemEditor
          item={item}
          plan={plan}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={(patch) => {
            run(() => api.studio.updatePlanItem(item.id, patch))
            setEditing(false)
          }}
        />
      )}
    </div>
  )
}

/** The classes an item can be tied to, „czw 23.10, 18:00”, plus the one it is tied to now. */
function classOptions(plan: StudioPlan, current: StudioPlanItem['booking']) {
  const group = plan.members.length > 1
  const name = (id: string | null) => firstName(plan.members.find((m) => m.clientId === id)?.name ?? '')
  const list = plan.classes.map((c) => ({ id: c.id, startsAt: c.startsAt, label: `${classLabel(c.startsAt)}${group && c.clientId ? ` · ${name(c.clientId)}` : ''}` }))
  if (current && !list.some((c) => c.id === current.id)) list.push({ id: current.id, startsAt: current.startsAt, label: classLabel(current.startsAt) })
  return list.sort((a, b) => a.startsAt.localeCompare(b.startsAt))
}

function ClassSelect({ id, plan, current, value, onChange }: { id: string; plan: StudioPlan; current: StudioPlanItem['booking']; value: string; onChange: (v: string) => void }) {
  return (
    <select id={id} className="cl-input" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">bez zajęć</option>
      {classOptions(plan, current).map((c) => (
        <option key={c.id} value={c.id}>
          {c.label}
        </option>
      ))}
    </select>
  )
}

function ItemEditor({
  item,
  plan,
  busy,
  onCancel,
  onSave,
}: {
  item: StudioPlanItem
  plan: StudioPlan
  busy: boolean
  onCancel: () => void
  onSave: (patch: { note: string; bookingId: string | null }) => void
}) {
  const [note, setNote] = useState(item.note)
  const [booking, setBooking] = useState(item.booking?.id ?? '')
  return (
    <form
      className="grid gap-3 pt-1 desk:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault()
        onSave({ note: note.trim(), bookingId: booking || null })
      }}
    >
      <div>
        <FieldLabel htmlFor={`note-${item.id}`}>Notatka · widzi to uczeń</FieldLabel>
        <input id={`note-${item.id}`} className="cl-input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="np. przeczytaj dialog przed zajęciami" />
      </div>
      <div>
        <FieldLabel htmlFor={`class-${item.id}`}>Na zajęcia</FieldLabel>
        <ClassSelect id={`class-${item.id}`} plan={plan} current={item.booking} value={booking} onChange={setBooking} />
      </div>
      <div className="flex items-center gap-4 desk:col-span-2">
        <Button type="submit" variant="secondary" className="min-h-10 px-[18px] text-class-gloss" disabled={busy}>
          Zapisz
        </Button>
        <button type="button" className="cl-link-quiet text-class-gloss" onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </form>
  )
}

/* ── „+ Dodaj lekcję albo kurs” ────────────────────────────────────────── */

function AddItem({ outline, ...props }: { plan: StudioPlan; outline: CourseOutline | undefined; cache: Cache; onClose: () => void }) {
  if (!outline) return <p className="m-0 py-3 text-class-gloss text-class-ink-faint">Wczytuję kursy…</p>
  return <AddItemForm courses={outline} {...props} />
}

function AddItemForm({ plan, courses, cache, onClose }: { plan: StudioPlan; courses: CourseOutline; cache: Cache; onClose: () => void }) {
  const inPlan = useMemo(() => new Set(plan.items.map((i) => `${i.course.id}:${i.lesson?.id ?? ''}`)), [plan.items])
  const taken = (courseId: string, lessonId: string) => inPlan.has(`${courseId}:${lessonId}`)

  /** The next lesson after the plan's last one from this course; else the whole course; else the first free lesson. */
  const suggest = (courseId: string): string => {
    const course = courses.find((c) => c.id === courseId)
    if (!course) return ''
    const numbers = plan.items.filter((i) => i.course.id === courseId && i.lesson).map((i) => i.lesson!.number)
    const after = numbers.length ? Math.max(...numbers) : 0
    const next = course.lessons.find((l) => l.number > after && !taken(courseId, l.id))
    if (numbers.length && next) return next.id
    if (!taken(courseId, '')) return ''
    return course.lessons.find((l) => !taken(courseId, l.id))?.id ?? ''
  }

  const lastCourse = plan.items[plan.items.length - 1]?.course.id
  const [courseId, setCourseId] = useState(() => lastCourse ?? courses[0]?.id ?? '')
  const [lessonId, setLessonId] = useState(() => suggest(lastCourse ?? courses[0]?.id ?? ''))
  const [note, setNote] = useState('')
  const [booking, setBooking] = useState('')
  const [release, setRelease] = useState(false)

  const add = useMutation({
    mutationFn: () => api.studio.addPlanItem(plan.id, { courseId, lessonId: lessonId || null, note: note.trim(), bookingId: booking || null, released: release }),
    onSuccess: (p) => {
      cache.apply(p)
      onClose()
    },
  })

  const course = courses.find((c) => c.id === courseId)
  const id = `add-${plan.id}`

  return (
    <form
      className="cl-card mt-4 grid gap-4 p-5 desk:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (courseId && !taken(courseId, lessonId)) add.mutate()
      }}
    >
      <p className="m-0 font-semibold desk:col-span-2">Dodaj do planu</p>
      <div>
        <FieldLabel htmlFor={`${id}-course`}>Kurs</FieldLabel>
        <select
          id={`${id}-course`}
          className="cl-input"
          value={courseId}
          onChange={(e) => {
            setCourseId(e.target.value)
            setLessonId(suggest(e.target.value))
          }}
        >
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
              {c.published ? '' : ' (szkic)'}
            </option>
          ))}
        </select>
      </div>
      <div>
        <FieldLabel htmlFor={`${id}-lesson`}>Lekcja</FieldLabel>
        <select id={`${id}-lesson`} className="cl-input" value={lessonId} onChange={(e) => setLessonId(e.target.value)}>
          <option value="" disabled={taken(courseId, '')}>
            Cały kurs{taken(courseId, '') ? ' — już w planie' : ''}
          </option>
          {(course?.lessons ?? []).map((l) => (
            <option key={l.id} value={l.id} disabled={taken(courseId, l.id)}>
              Lekcja {l.number} · {l.title}
              {taken(courseId, l.id) ? ' — już w planie' : ''}
            </option>
          ))}
        </select>
      </div>
      <div>
        <FieldLabel htmlFor={`${id}-note`}>Notatka (opcjonalnie) · widzi to uczeń</FieldLabel>
        <input id={`${id}-note`} className="cl-input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="np. przeczytaj dialog przed zajęciami" />
      </div>
      <div>
        <FieldLabel htmlFor={`${id}-class`}>Na zajęcia (opcjonalnie)</FieldLabel>
        <ClassSelect id={`${id}-class`} plan={plan} current={null} value={booking} onChange={setBooking} />
      </div>
      <label className="flex items-center gap-2 text-class-gloss desk:col-span-2">
        <input type="checkbox" checked={release} onChange={(e) => setRelease(e.target.checked)} className="h-4 w-4 accent-[var(--color-class-sky-700)]" />
        Udostępnij od razu
      </label>
      {add.isError && (
        <div className="desk:col-span-2">
          <ErrorLine>{errorText(add.error)}</ErrorLine>
        </div>
      )}
      <div className="flex items-center gap-4 desk:col-span-2">
        <Button type="submit" variant="secondary" disabled={!courseId || taken(courseId, lessonId) || add.isPending}>
          {add.isPending ? 'Dodaję…' : 'Dodaj'}
        </Button>
        <button type="button" className="cl-link-quiet" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  )
}
